<template>
    <FormModal
        :visible="visible"
        :title="isEditing ? 'Editar procurador' : 'Adicionar procurador'"
        :icon="require('@/assets/addRepresentante.png')"
        :loading="loading"
        :confirm-label="isEditing ? 'Atualizar' : 'Adicionar'"
        @update:visible="handleClose"
        @confirm="handleSubmit"
        @cancel="handleClose"
    >
        <form @submit.prevent="handleSubmit" class="space-y-6">
            <!-- SUBSECTION: Dados Pessoais -->
            <div class="form-subsection">
                <h4 class="form-subsection-title">
                    <i class="fa fa-user"></i>
                    Dados Pessoais
                </h4>

                <div class="form-grid-1">
                    <div class="form-field">
                        <InputNome 
                            v-model="localData.Nome"
                            label="Nome"
                            :required="true"
                            :erro="camposObrigatorios.erroNome"
                        />
                    </div>
                </div>

                <div class="form-grid-2">
                    <div class="form-field">
                        <label class="form-field-label form-field-label-required" for="cpf">CPF/CNPJ</label>
                        <input 
                            class="field" 
                            type="text" 
                            id="cpf" 
                            v-model="localData.Cpf"
                        />
                        <p v-if="camposObrigatorios.erroCpf" class="form-field-error">Campo obrigatório</p>
                    </div>
                    <div class="form-field">
                        <label class="form-field-label form-field-label-required" for="email">E-mail</label>
                        <input 
                            class="field" 
                            type="text" 
                            id="email" 
                            v-model="localData.Email"
                            @blur="validarEmail"
                        />
                        <p v-if="emailErros.email" class="form-field-error">{{ emailErros.email }}</p>
                    </div>
                </div>

                <div class="form-grid-2">
                    <div class="form-field">
                        <label class="form-field-label" for="cargo">Cargo</label>
                        <input class="field" type="text" id="cargo" v-model="localData.Cargo" />
                    </div>
                    <div class="form-field">
                        <telefone-internacional
                            v-model="localData.Telefone" 
                            label="Telefone" 
                            name="Telefone"
                            :opcional="true"
                            @validate="onValidateTelefone"
                        />
                    </div>
                </div>
            </div>

            <div class="form-subsection-divider"></div>

            <!-- SUBSECTION: Endereço -->
            <div class="form-subsection">
                <h4 class="form-subsection-title">
                    <i class="fa fa-map-marker"></i>
                    Endereço
                </h4>

                <EnderecoFields
                    ref="endereco"
                    v-model="localData"
                    :countries="listaPaises"
                    id-prefix="procurador-fi-address"
                />
            </div>

            <div class="form-subsection-divider"></div>

            <!-- SUBSECTION: Informações Regulatórias -->
            <div class="form-subsection">
                <h4 class="form-subsection-title">
                    <i class="fa fa-shield"></i>
                    Informações Regulatórias
                </h4>

                <div class="form-grid-2">
                    <div class="form-field">
                        <label class="form-field-label form-field-label-required">É considerado US Person?</label>
                        <div class="form-radio-group">
                            <label class="form-radio-item">
                                <input type="radio" value="false" name="usPerson" class="form-radio-input" 
                                    v-model="localData.UsPerson" />
                                <span class="form-radio-label">Não</span>
                            </label>
                            <label class="form-radio-item">
                                <input type="radio" value="true" name="usPerson" class="form-radio-input" 
                                    v-model="localData.UsPerson" />
                                <span class="form-radio-label">Sim</span>
                            </label>
                        </div>
                    </div>
                    <div class="form-field">
                        <label class="form-field-label form-field-label-required">É uma Pessoa Exposta Politicamente?</label>
                        <div class="form-radio-group">
                            <label class="form-radio-item">
                                <input type="radio" value="false" name="politicamenteExposta" class="form-radio-input" 
                                    v-model="localData.PoliticamenteExposta" />
                                <span class="form-radio-label">Não</span>
                            </label>
                            <label class="form-radio-item">
                                <input type="radio" value="true" name="politicamenteExposta" class="form-radio-input" 
                                    v-model="localData.PoliticamenteExposta" />
                                <span class="form-radio-label">Sim</span>
                            </label>
                        </div>
                    </div>
                </div>

                <div class="form-grid-1">
                    <div class="form-field">
                        <label class="form-field-label form-field-label-required" for="assinatura">Assinatura</label>
                        <ModalSelect id="assinatura" class="field" v-model="localData.Assinatura"
                          :options="[{ id: '', descricao: 'Selecione' }, { id: '1', descricao: 'Não assina pela empresa' }, { id: '2', descricao: 'Assina isoladamente' }, { id: '0', descricao: 'Assina em conjunto' }]"
                          title="Assinatura" placeholder="Selecione" required
                        />
                        <p v-if="camposObrigatorios.erroAssinatura" class="form-field-error">Campo obrigatório</p>
                    </div>
                </div>
            </div>
        </form>
    </FormModal>
</template>

<script>
import ModalSelect from "@/components/ModalSelect.vue";
import { mapGetters } from 'vuex';
import FormModal from './FormModal.vue';
import EnderecoFields from './EnderecoFields.vue';
import emailValidatorMixin from '@/utils/emailValidatorMixin.js';
import { isCNPJ } from "@/utils/documentoUtils";

export default {
    name: 'ModalProcuradorFI',
    components: {
        ModalSelect,
        FormModal,
        EnderecoFields
    },
    mixins: [emailValidatorMixin],
    props: {
        visible: {
            type: Boolean,
            default: false
        },
        procurador: {
            type: Object,
            default: null
        },
        loading: {
            type: Boolean,
            default: false
        }
    },
    computed: {
        ...mapGetters({
            listaPaises: 'GET_LISTA_PAISES',
        }),
        isEditing() {
            return this.localData.Guid && this.localData.Guid !== '';
        }
    },
    data() {
        return {
            telefoneValido: {},
            emailErros: {},
            camposObrigatorios: {
                erroNome: false,
                erroCpf: false,
                erroEmail: false,
                erroTelefone: false,
                erroCep: false,
                erroLogradouro: false,
                erroNumero: false,
                erroBairro: false,
                erroCidade: false,
                erroUF: false,
                erroPais: false,
                erroAssinatura: false
            },
            localData: {
                Guid: '',
                Nome: '',
                Cpf: '',
                Email: '',
                Cargo: '',
                Telefone: '',
                Cep: '',
                Logradouro: '',
                Numero: '',
                Complemento: '',
                Bairro: '',
                Cidade: '',
                Uf: '',
                Pais: '',
                Tipo: 'Procurador',
                EmissorOrdem: false,
                UsPerson: false,
                PoliticamenteExposta: false,
                Assinatura: ''
            }
        };
    },
    watch: {
        procurador: {
            immediate: true,
            handler(newVal) {
                if (newVal) {
                    this.localData = JSON.parse(JSON.stringify(newVal));
                    if (isCNPJ(this.localData.Cpf)) {
                        this.localData.Cpf = '';
                    }
                    this.$nextTick(() => this.$refs.endereco?.refreshLookup());
                } else {
                    this.resetForm();
                }
            }
        },
        visible(newVal) {
            if (!newVal) {
                this.resetValidation();
            }
        }
    },
    methods: {
        onValidateTelefone({ name, valid }) {
            if (!this.telefoneValido) {
                this.telefoneValido = {};
            }
            if (valid !== 'undefined') {
                this.telefoneValido[name] = valid;
            }
        },
        validarEmail() {
            this.validarEmailGeneric(this.localData.Email, 'email', {
                obrigatorio: true,
            });
        },
        validarFormulario() {
            let permitirAvancar = true;
            let nomeDoCampo = '';
            let valorInvalido = false;

            this.camposObrigatorios = {
                erroNome: false,
                erroCpf: false,
                erroEmail: false,
                erroTelefone: false,
                erroCep: false,
                erroLogradouro: false,
                erroNumero: false,
                erroBairro: false,
                erroCidade: false,
                erroUF: false,
                erroPais: false,
                erroAssinatura: false
            };

            if (!this.localData.Nome) {
                this.camposObrigatorios.erroNome = true;
                nomeDoCampo = 'Nome';
                permitirAvancar = false;
            }

            if (!this.localData.Cpf) {
                this.camposObrigatorios.erroCpf = true;
                nomeDoCampo = nomeDoCampo || 'CPF/CNPJ';
                permitirAvancar = false;
            }

            if (Object.values(this.emailErros).some(erro => erro !== '')) {
                this.camposObrigatorios.erroEmail = true;
                nomeDoCampo = nomeDoCampo || 'E-mail';
                permitirAvancar = false;
            }

            const telefoneValido = this.telefoneValido?.Telefone;
            if (this.localData.Telefone?.length > 4 && !telefoneValido) {
                nomeDoCampo = nomeDoCampo || 'Telefone';
                valorInvalido = true;
                permitirAvancar = false;
            }

            const addressValidation = this.$refs.endereco?.validate(true);
            if (!addressValidation?.valid) {
                nomeDoCampo = nomeDoCampo || addressValidation?.firstInvalidLabel || 'País';
                permitirAvancar = false;
            }

            if (this.localData.Assinatura === null || this.localData.Assinatura === undefined || this.localData.Assinatura === '') {
                this.camposObrigatorios.erroAssinatura = true;
                nomeDoCampo = nomeDoCampo || 'Assinatura';
                permitirAvancar = false;
            }

            if (!permitirAvancar && nomeDoCampo) {
                if (valorInvalido) {
                    this.$swal({
                        icon: 'warning',
                        title: 'Oops...',
                        text: `O campo ${nomeDoCampo} foi informado, mas não foi preenchido corretamente. Preencha a informação para avançar.`,
                    });
                } else {
                    this.$swal({
                        icon: 'warning',
                        title: 'Oops...',
                        text: `O campo ${nomeDoCampo} não foi informado, preencha a informação para avançar.`,
                    });
                }
            }

            return permitirAvancar;
        },
        handleSubmit() {
            if (isCNPJ(this.localData.Cpf)) {
                this.$alertWarning(
                    'Documento inválido',
                    'O tipo de documento CNPJ não é permitido para o procurador. Selecione CPF, CNH ou RNE antes de salvar.'
                );
                return;
            }
            if (this.validarFormulario()) {
                this.$emit('submit', this.localData);
            }
        },
        handleClose() {
            this.$emit('update:visible', false);
            this.$emit('close');
        },
        resetForm() {
            this.localData = {
                Guid: '',
                Nome: '',
                Cpf: '',
                Email: '',
                Cargo: '',
                Telefone: '',
                Cep: '',
                Logradouro: '',
                Numero: '',
                Complemento: '',
                Bairro: '',
                Cidade: '',
                Uf: '',
                Pais: '',
                Tipo: 'Procurador',
                EmissorOrdem: false,
                UsPerson: false,
                PoliticamenteExposta: false,
                Assinatura: ''
            };
            this.resetValidation();
        },
        resetValidation() {
            this.camposObrigatorios = {
                erroNome: false,
                erroCpf: false,
                erroEmail: false,
                erroTelefone: false,
                erroCep: false,
                erroLogradouro: false,
                erroNumero: false,
                erroBairro: false,
                erroCidade: false,
                erroUF: false,
                erroPais: false,
                erroAssinatura: false
            };
            this.emailErros = {};
            this.$refs.endereco?.resetValidation();
        }
    }
}
</script>
